import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { Cross2Icon } from "@radix-ui/react-icons";
import Button from "@/components/atoms/Button";
import useAccess from "@/hooks/useAccess";

interface TypeSelectionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const TypeSelectionModal: React.FC<TypeSelectionModalProps> = ({ isOpen, onOpenChange }) => {
  const { getAccess } = useAccess();
  const [selectedOption, setSelectedOption] = React.useState<string>("auto_invoice");

  const handleCreateInvoice = () => {
    const urlMap: { [key: string]: string } = {
      auto_invoice: "/auto_invoices/new/auto_invoice",
      credit_note: "/auto_invoices/new/credit_note",
      proforma: "/auto_invoices/new/proforma",
      manual_invoice: "/auto_invoices/new/manual_invoice",
      manual_credit_note: "/auto_invoices/new/manual_credit_note",
    };

    const selectedUrl = urlMap[selectedOption];
    if (selectedUrl) {
      window.location.href = selectedUrl;
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 dark:bg-black/60 z-40 transition-opacity" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-background p-6 rounded-lg shadow-lg max-w-md w-full transition-colors">
          <Dialog.Title className="text-lg font-bold text-gray-900 dark:text-darkTextColor">Create Invoice</Dialog.Title>
          <Dialog.Description className="text-sm text-gray-500 dark:text-darkTextColor mb-4">
            Select a type of invoice to create.
          </Dialog.Description>

          {/* RadioGroup for selecting the invoice type */}
          <RadioGroup.Root value={selectedOption} onValueChange={setSelectedOption} className="flex flex-col gap-2.5">
            <div className="flex items-center">
              <RadioGroup.Item
                className="w-6 h-6 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700 shadow-md outline-none hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 data-[state=checked]:bg-primary"
                value="auto_invoice"
                id="auto_invoice"
              >
                <RadioGroup.Indicator className="flex items-center justify-center w-full h-full rounded-full bg-blue-500" />
              </RadioGroup.Item>
              <label htmlFor="auto_invoice" className="pl-3 text-sm leading-none text-gray-900 dark:text-darkTextColor cursor-pointer">
                Auto Invoice
              </label>
            </div>

            {getAccess(["create_credit_note"]) && (
              <div className="flex items-center">
                <RadioGroup.Item
                  className="w-6 h-6 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700 shadow-md outline-none hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 data-[state=checked]:bg-primary"
                  value="credit_note"
                  id="credit_note"
                >
                  <RadioGroup.Indicator className="flex items-center justify-center w-full h-full rounded-full bg-blue-500" />
                </RadioGroup.Item>
                <label htmlFor="credit_note" className="pl-3 text-sm leading-none text-gray-900 dark:text-darkTextColor cursor-pointer">
                  Credit Note
                </label>
              </div>
            )}

            {getAccess(["create_proforma"]) && (
              <div className="flex items-center">
                <RadioGroup.Item
                  className="w-6 h-6 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700 shadow-md outline-none hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 data-[state=checked]:bg-primary"
                  value="proforma"
                  id="proforma"
                >
                  <RadioGroup.Indicator className="flex items-center justify-center w-full h-full rounded-full bg-blue-500" />
                </RadioGroup.Item>
                <label htmlFor="proforma" className="pl-3 text-sm leading-none text-gray-900 dark:text-darkTextColor cursor-pointer">
                  Proforma
                </label>
              </div>
            )}

            {getAccess(["create_manual_invoice"]) && (
              <div className="flex items-center">
                <RadioGroup.Item
                  className="w-6 h-6 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700 shadow-md outline-none hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 data-[state=checked]:bg-primary"
                  value="manual_invoice"
                  id="manual_invoice"
                >
                  <RadioGroup.Indicator className="flex items-center justify-center w-full h-full rounded-full bg-blue-500" />
                </RadioGroup.Item>
                <label htmlFor="manual_invoice" className="pl-3 text-sm leading-none text-gray-900 dark:text-darkTextColor cursor-pointer">
                  Manual Invoice
                </label>
              </div>
            )}

            {getAccess(["create_manual_credit_note"]) && (
              <div className="flex items-center">
                <RadioGroup.Item
                  className="w-6 h-6 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700 shadow-md outline-none hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 data-[state=checked]:bg-primary"
                  value="manual_credit_note"
                  id="manual_credit_note"
                >
                  <RadioGroup.Indicator className="flex items-center justify-center w-full h-full rounded-full bg-blue-500" />
                </RadioGroup.Item>
                <label htmlFor="manual_credit_note" className="pl-3 text-sm leading-none text-gray-900 dark:text-darkTextColor cursor-pointer">
                  Manual Credit Note
                </label>
              </div>
            )}
          </RadioGroup.Root>

          {/* Buttons */}
          <div className="flex justify-end mt-4 space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateInvoice}>Create</Button>
          </div>

          {/* Close Button */}
          <Dialog.Close asChild>
            <button
              className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              aria-label="Close"
            >
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default TypeSelectionModal;

